import { useRef, RefObject, useMemo } from 'react';
import { useInteractOutside } from 'react-aria';

import { MenuListContextType } from './utils';

/**
 * Хук для работы корня меню. Создаёт значения для контекстов и обрабатывает клики по вложенным подменю
 * @returns реф для триггера меню, реф попапа меню, значение для MenuListContext
 * @example
 * ```tsx
 * function MenuTrigger() {
 *  const { isOpen, toggle, close } = useToggle();
 *  const { triggerRef, popupRef, contextValue } = useMenuRoot({ close });
 *
 *  return (
 *      <>
 *          <MyTrigger onClick={toggle} ref={triggerRef} />
 *          <Popup isOpen={isOpen}>
 *              <MenuListContext.Provider value={contextValue}>
 *                  <MyMenu />
 *              </MenuListContext.Provider>
 *          </Popup>
 *      </>
 *  )
 * }
 * ```
 */
export function useMenuRoot({
    close,
    shouldCloseOnInteractOutside,
}: {
    close: () => void;
    /** Дополнительное условие закрытия по клику вне меню: при false меню остаётся открытым */
    shouldCloseOnInteractOutside?: (element: HTMLElement) => boolean;
}) {
    const triggerRef = useRef<HTMLElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const levels = useRef<RefObject<HTMLDivElement>[]>([]);

    const contextValue = useMemo<MenuListContextType>(
        () => ({
            triggerRef,
            levels,
            onItemClick: close,
        }),
        [close],
    );

    // Закрываем меню при клике вне его, кроме триггера и дочерних подменю
    useInteractOutside({
        ref: popupRef,
        onInteractOutside: event => {
            const target = event.target;

            if (!(target instanceof HTMLElement)) {
                return;
            }

            if (triggerRef.current?.contains(target)) {
                return;
            }

            if (levels.current.some(level => level.current?.contains(target))) {
                return;
            }

            if (shouldCloseOnInteractOutside?.(target) === false) {
                return;
            }

            close();
        },
    });

    return { triggerRef, popupRef, contextValue };
}
